/**
 * Phase 4: read the backend's decision trace and skip marker, and put them
 * into words.
 *
 * After a run the Switch node's result metadata carries ``decision`` (the
 * cases it evaluated, the values it saw, and why it stopped), written by
 * ``services/workflow_ir/decision_trace.py``. Nodes behind a branch the
 * Switch did not take carry a skip marker written by
 * ``temporal/utils/branch_gating.py``. Every reader here tolerates older
 * results that lack the new fields (then only the Phase 1 "Took «label»"
 * remains).
 */
import type { JsonValue } from './functional-control-flow';
import { OPERATOR_LABELS } from './switch-condition-builder';

export type DecisionLeafOp =
  | 'eq'
  | 'ne'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'in'
  | 'not_in'
  | 'exists'
  | 'is_null'
  | 'literal';

export interface DecisionLeaf {
  /** Field read from the input, with the ``value`` root removed; null when the left side is not a field. */
  path: string | null;
  op: DecisionLeafOp | string;
  /** The literal the field was compared with (absent for ``exists`` / ``is_null``). */
  expected?: JsonValue;
  /** What the input held at ``path`` when the case ran (truncated by the backend). */
  seen?: JsonValue;
  /** true / false, or null when the leaf was not evaluated or errored. */
  result: boolean | null;
  /** Expression error code when the leaf could not be evaluated (``path_missing`` …). */
  error?: string | null;
  negated?: boolean;
}

export interface DecisionCase {
  case_index: number;
  label: string;
  /** true = matched, false = did not match, null = not evaluated (a previous case matched or errored). */
  matched: boolean | null;
  combine: 'and' | 'or' | null;
  conditions: DecisionLeaf[];
}

export type DecisionReason =
  | 'matched'
  | 'default'
  | 'null_default'
  | 'error'
  // --- if/else (Phase 8b): the field was missing or incomparable, so the
  // Otherwise branch ran. `routed_by` says which field.
  | 'missing_field_else';

export interface DecisionRoutedBy {
  code: string;
  field: string | null;
  message: string;
}

export interface SwitchDecision {
  selected_label: string | null;
  matched_case_index: number | null;
  is_default: boolean;
  reason: DecisionReason;
  stopped_at_case: number | null;
  evaluated: DecisionCase[];
  truncated: boolean;
  error_code?: string | null;
  // --- if/else (Phase 8b), additive ---
  /** 'then' | 'else' for an If, independent of renamed labels. */
  branch?: 'then' | 'else' | null;
  branches?: { then: string; else: string } | null;
  routed_by?: DecisionRoutedBy | null;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asLeaf = (value: unknown): DecisionLeaf | null => {
  const record = asRecord(value);
  if (!record || typeof record.op !== 'string') return null;
  return {
    path: typeof record.path === 'string' ? record.path : null,
    op: record.op,
    expected: record.expected as JsonValue | undefined,
    seen: record.seen as JsonValue | undefined,
    result: typeof record.result === 'boolean' ? record.result : null,
    error: typeof record.error === 'string' ? record.error : null,
    negated: record.negated === true,
  };
};

const asCase = (value: unknown): DecisionCase | null => {
  const record = asRecord(value);
  if (
    !record ||
    typeof record.case_index !== 'number' ||
    typeof record.label !== 'string'
  ) {
    return null;
  }
  return {
    case_index: record.case_index,
    label: record.label,
    matched: typeof record.matched === 'boolean' ? record.matched : null,
    combine:
      record.combine === 'and' || record.combine === 'or'
        ? record.combine
        : null,
    conditions: Array.isArray(record.conditions)
      ? record.conditions
          .map(asLeaf)
          .filter((leaf): leaf is DecisionLeaf => leaf !== null)
      : [],
  };
};

/** The Switch's decision trace from a node result, or null when absent. */
export const getSwitchDecision = (result: unknown): SwitchDecision | null => {
  const root = asRecord(result);
  if (!root) return null;
  const metadata = asRecord(root.metadata);
  const raw = asRecord(metadata?.decision) ?? asRecord(root.decision);
  if (!raw) return null;
  const reason = raw.reason;
  return {
    selected_label:
      typeof raw.selected_label === 'string' ? raw.selected_label : null,
    matched_case_index:
      typeof raw.matched_case_index === 'number'
        ? raw.matched_case_index
        : null,
    is_default: raw.is_default === true,
    reason:
      reason === 'matched' ||
      reason === 'default' ||
      reason === 'null_default' ||
      reason === 'error' ||
      reason === 'missing_field_else'
        ? reason
        : 'matched',
    stopped_at_case:
      typeof raw.stopped_at_case === 'number' ? raw.stopped_at_case : null,
    evaluated: Array.isArray(raw.evaluated)
      ? raw.evaluated
          .map(asCase)
          .filter((entry): entry is DecisionCase => entry !== null)
      : [],
    truncated: raw.truncated === true,
    error_code: typeof raw.error_code === 'string' ? raw.error_code : null,
    // --- if/else (Phase 8b) ---
    branch: raw.branch === 'then' || raw.branch === 'else' ? raw.branch : null,
    branches: (() => {
      const record = asRecord(raw.branches);
      return record &&
        typeof record.then === 'string' &&
        typeof record.else === 'string'
        ? { then: record.then, else: record.else }
        : null;
    })(),
    routed_by: (() => {
      const record = asRecord(raw.routed_by);
      return record && typeof record.code === 'string'
        ? {
            code: record.code,
            field: typeof record.field === 'string' ? record.field : null,
            message: typeof record.message === 'string' ? record.message : '',
          }
        : null;
    })(),
    // --- end if/else ---
  };
};

const MAX_INLINE = 60;

/** A JSON value as a short, quoted-when-text literal for a sentence. */
export const formatValue = (value: JsonValue | undefined): string => {
  if (value === undefined) return '(nothing)';
  let text: string;
  if (typeof value === 'string') {
    text = JSON.stringify(value);
  } else {
    try {
      text = JSON.stringify(value) ?? 'null';
    } catch {
      text = String(value);
    }
  }
  return text.length > MAX_INLINE ? `${text.slice(0, MAX_INLINE - 1)}…` : text;
};

const leafOperatorWords = (leaf: DecisionLeaf): string => {
  const op = leaf.op;
  if (op === 'exists') return leaf.negated ? 'does not exist' : 'exists';
  if (op === 'is_null') {
    return leaf.negated ? 'is not empty' : 'is empty (null)';
  }
  const base = (OPERATOR_LABELS as Record<string, string>)[op] ?? op;
  return leaf.negated ? `not (${base})` : base;
};

const leafSubject = (leaf: DecisionLeaf): string =>
  leaf.path && leaf.path.length ? leaf.path : 'the value';

/** The condition in words: 'verdict equals "proceed"'. */
export const describeLeafCondition = (leaf: DecisionLeaf): string => {
  const words = leafOperatorWords(leaf);
  if (leaf.op === 'exists' || leaf.op === 'is_null') {
    return `${leafSubject(leaf)} ${words}`;
  }
  if (leaf.op === 'literal') {
    return leaf.result === null
      ? 'a fixed value'
      : leaf.result
        ? 'always'
        : 'never';
  }
  return `${leafSubject(leaf)} ${words} ${formatValue(leaf.expected)}`;
};

/** What the input held: 'saw "merged"' / 'the field was missing'. */
export const describeLeafSeen = (leaf: DecisionLeaf): string => {
  if (leaf.error === 'path_missing') return 'the field was missing';
  if (leaf.error === 'path_type_mismatch') {
    return 'the path could not be followed';
  }
  if (leaf.error === 'comparison_type_mismatch') {
    return `saw ${formatValue(leaf.seen)} (a different type)`;
  }
  if (leaf.error) return `could not be checked (${leaf.error})`;
  if (leaf.op === 'exists') {
    return leaf.seen === undefined ? 'the field was missing' : 'it was present';
  }
  if (leaf.seen === undefined) return 'nothing to compare';
  return `saw ${formatValue(leaf.seen)}`;
};

/** One leaf as 'condition → yes/no (saw …)'. */
export const describeLeafOutcome = (leaf: DecisionLeaf): string => {
  const outcome =
    leaf.result === null ? 'not checked' : leaf.result ? 'yes' : 'no';
  return `${describeLeafCondition(leaf)} → ${outcome} (${describeLeafSeen(leaf)})`;
};

/**
 * The short clause after "because": for a matched case the conditions that
 * held ('verdict = "proceed"'); otherwise why the default was taken.
 */
export const decisionBecauseClause = (decision: SwitchDecision): string => {
  if (decision.reason === 'null_default') {
    return 'the value was null';
  }
  // --- if/else (Phase 8b) ---
  if (decision.reason === 'missing_field_else') {
    const field = decision.routed_by?.field;
    if (decision.routed_by?.code === 'comparison_type_mismatch') {
      return field
        ? `${field} could not be compared`
        : 'the values could not be compared';
    }
    return field ? `${field} is missing` : 'the field is missing';
  }
  if (decision.reason === 'default' && decision.branch === 'else') {
    const only = decision.evaluated[0];
    const failed =
      only?.conditions.filter((leaf) => leaf.result === false) ?? [];
    const shown = failed.slice(0, 2).map((leaf) => {
      if (leaf.op === 'eq' && leaf.path) {
        return `${leaf.path} was ${formatValue(leaf.seen)}`;
      }
      return `${describeLeafCondition(leaf)} did not hold (${describeLeafSeen(leaf)})`;
    });
    return shown.length ? shown.join(' and ') : 'the condition did not hold';
  }
  // --- end if/else ---
  if (decision.reason === 'default') {
    const count = decision.evaluated.filter(
      (entry) => entry.matched === false,
    ).length;
    return count === 1
      ? 'the only case did not match'
      : `none of ${count || 'the'} cases matched`;
  }
  if (decision.reason === 'error') {
    return 'a case could not be evaluated';
  }
  const matched = decision.evaluated.find((entry) => entry.matched === true);
  if (!matched) return '';
  const held = matched.conditions.filter((leaf) => leaf.result === true);
  const shown = (held.length ? held : matched.conditions).slice(0, 3);
  const parts = shown.map((leaf) => {
    if (leaf.op === 'eq' && leaf.path) {
      return `${leaf.path} = ${formatValue(leaf.seen ?? leaf.expected)}`;
    }
    if (leaf.op === 'literal') return describeLeafCondition(leaf);
    return `${describeLeafCondition(leaf)} (${describeLeafSeen(leaf)})`;
  });
  const joiner = matched.combine === 'or' ? ' or ' : ' and ';
  const suffix =
    matched.conditions.length > shown.length
      ? ` and ${matched.conditions.length - shown.length} more`
      : '';
  return parts.join(joiner) + suffix;
};

/**
 * 'Took "proceed" because verdict = "proceed"'; falls back to 'Took "label"'
 * when there is no trace, and to null when the node has not run.
 */
export const switchTookSentence = (
  decision: SwitchDecision | null,
  fallbackLabel: string | null,
): string | null => {
  const label = decision?.selected_label ?? fallbackLabel;
  if (!label) return null;
  if (!decision) return `Took "${label}"`;
  const because = decisionBecauseClause(decision);
  return because ? `Took "${label}" because ${because}` : `Took "${label}"`;
};

// --- skipped nodes ----------------------------------------------------------

export interface SkipExplanation {
  switchId: string | null;
  /** Switch display name, when the marker carries it (Phase 4 backends). */
  switchName: string | null;
  selectedLabel: string | null;
  /** Branch labels, leaving the Switch, that would have reached this node. */
  notTakenLabels: string[];
}

/** Read the branch-gating skip marker; null when the node was not skipped by a Switch. */
export const getSkipExplanation = (result: unknown): SkipExplanation | null => {
  const root = asRecord(result);
  if (!root) return null;
  const metadata = asRecord(root.metadata);
  const gating = asRecord(metadata?.branch_gating);
  const skipped =
    root.status === 'skipped' ||
    root.reason === 'skipped_by_switch' ||
    gating?.reason === 'skipped_by_switch';
  if (!skipped) return null;
  const skippedBy = asRecord(gating?.skipped_by) ?? asRecord(root.skipped_by);
  const labelsRaw =
    skippedBy?.branch_labels_not_taken ?? root.branch_labels_not_taken;
  const notTakenLabels = Array.isArray(labelsRaw)
    ? labelsRaw.filter((entry): entry is string => typeof entry === 'string')
    : [];
  const pick = (...candidates: unknown[]): string | null =>
    candidates.find(
      (candidate): candidate is string =>
        typeof candidate === 'string' && candidate.length > 0,
    ) ?? null;
  return {
    switchId: pick(
      skippedBy?.switch_node_id,
      gating?.switch_id,
      root.switch_id,
    ),
    switchName: pick(skippedBy?.switch_name, root.switch_name),
    selectedLabel: pick(
      skippedBy?.selected_label,
      gating?.selected_label,
      root.selected_label,
    ),
    notTakenLabels,
  };
};

const quoteList = (labels: string[]): string =>
  labels.map((label) => `«${label}»`).join(', ');

/**
 * 'skipped: branch «short_circuit» not taken (Claim Switch took «proceed»)'.
 * ``switchName`` may be supplied by the caller when the marker lacks it.
 */
export const skippedSentence = (
  explanation: SkipExplanation,
  switchName: string | null = explanation.switchName,
): string => {
  const branch =
    explanation.notTakenLabels.length === 0
      ? 'branch not taken'
      : explanation.notTakenLabels.length === 1
        ? `branch ${quoteList(explanation.notTakenLabels)} not taken`
        : `branches ${quoteList(explanation.notTakenLabels)} not taken`;
  const who = switchName ?? 'the Switch';
  const took = explanation.selectedLabel
    ? ` (${who} took «${explanation.selectedLabel}»)`
    : '';
  return `skipped: ${branch}${took}`;
};

// --- Classify / Decide ------------------------------------------------------

export interface ClassifySummary {
  label: string | null;
  confidence: number | null;
  rationale: string | null;
  reasonCodes: string[];
}

/** The Classify / Decide outcome from its result (Phase 4 metadata first, then the data envelope). */
export const getClassifySummary = (result: unknown): ClassifySummary | null => {
  const root = asRecord(result);
  if (!root) return null;
  const metadata = asRecord(root.metadata);
  const data = asRecord(root.output_data) ?? asRecord(root.data);
  const evidence = asRecord(data?.score_criteria_evidence);
  const inner = asRecord(data?.result);
  const label =
    (typeof metadata?.decision_label === 'string' && metadata.decision_label) ||
    (typeof data?.decision === 'string' && data.decision) ||
    (typeof inner?.decision === 'string' && inner.decision) ||
    null;
  if (!label) return null;
  const confidenceRaw =
    metadata?.confidence ?? evidence?.confidence ?? inner?.confidence;
  const rationaleRaw =
    metadata?.rationale ?? data?.feedback ?? inner?.explanation;
  const codesRaw = metadata?.reason_codes ?? evidence?.reason_codes;
  return {
    label,
    confidence: typeof confidenceRaw === 'number' ? confidenceRaw : null,
    rationale: typeof rationaleRaw === 'string' ? rationaleRaw : null,
    reasonCodes: Array.isArray(codesRaw)
      ? codesRaw.filter((code): code is string => typeof code === 'string')
      : [],
  };
};
